import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from './api';

const ENT_KEY = 'pintarplay_ent_cache';
const EntCtx = createContext(null);

function readCache() {
  try {
    const c = JSON.parse(localStorage.getItem(ENT_KEY));
    if (!c) return null;
    const fresh = Date.now() - c.fetchedAt < 72 * 3600 * 1000;
    const notExpired = !c.entitlement.accessExpiresAt || new Date(c.entitlement.accessExpiresAt) > new Date();
    if (c.entitlement.plan === 'PRO' && (!fresh || !notExpired)) return { ...c, entitlement: { ...c.entitlement, plan: 'FREE' } };
    return c;
  } catch { return null; }
}

export function EntitlementProvider({ children }) {
  const [user, setUser] = useState(undefined);
  const [entitlement, setEntitlement] = useState(readCache()?.entitlement || { plan: 'FREE' });
  const [transactions, setTransactions] = useState([]);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem('pp_token')) {
      setUser(null);
      const c = readCache();
      if (!c) setEntitlement({ plan: 'FREE' });
      return;
    }
    try {
      const me = await api.get('/auth/me');
      setUser(me.data);
      const r = await api.get('/billing/subscription-status');
      setEntitlement(r.data.entitlement);
      setTransactions(r.data.transactions || []);
      localStorage.setItem(ENT_KEY, JSON.stringify({ entitlement: r.data.entitlement, fetchedAt: Date.now() }));
    } catch {
      setUser(null);
      const c = readCache();
      if (!c) setEntitlement({ plan: 'FREE' });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const authAction = async (path, phone, password, extras = {}) => {
    const r = await api.post(`/auth/${path}`, { phone, password, ...extras });
    localStorage.setItem('pp_token', r.data.token);
    api.defaults.headers.common.Authorization = `Bearer ${r.data.token}`;
    await refresh();
    return r.data;
  };

  useEffect(() => {
    const tok = localStorage.getItem('pp_token');
    if (tok) api.defaults.headers.common.Authorization = `Bearer ${tok}`;
  }, []);

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('pp_token');
    localStorage.removeItem(ENT_KEY);
    delete api.defaults.headers.common.Authorization;
    setUser(null);
    setEntitlement({ plan: 'FREE' });
  };

  return (
    <EntCtx.Provider value={{ user, entitlement, transactions, refresh, logout,
      login: (e, p) => authAction('login', e, p),
      register: (e, p, weeklyWhatsappConsent = false) => authAction(
        'register', e, p, { weeklyWhatsappConsent }) }}>
      {children}
    </EntCtx.Provider>
  );
}

export const useEntitlement = () => useContext(EntCtx);
